import {
  Rrule_Day,
  Rrule_Frequency,
  type RRuleSet,
  type RRule,
} from "@/graphql/generated-types";
import type { RRuleState, RruleDaysState } from "@/types";
import { DEFAULT_RRULE_DAYS_STATE } from "@/consts";
import { toUnix } from "@/stories/utils/dates";

const FREQ_TO_CADENCE: Record<Rrule_Frequency, number> = {
  [Rrule_Frequency.Daily]: 1,
  [Rrule_Frequency.Weekly]: 7,
  [Rrule_Frequency.Monthly]: 30,
  [Rrule_Frequency.Yearly]: 365,
  [Rrule_Frequency.Hourly]: 1,
  [Rrule_Frequency.Minutely]: 1,
  [Rrule_Frequency.Secondly]: 1,
};

const RRULE_DAY_TO_WEEKDAY: Record<Rrule_Day, number> = {
  [Rrule_Day.Mo]: 0,
  [Rrule_Day.Tu]: 1,
  [Rrule_Day.We]: 2,
  [Rrule_Day.Th]: 3,
  [Rrule_Day.Fr]: 4,
  [Rrule_Day.Sa]: 5,
  [Rrule_Day.Su]: 6,
};

const parseDateTime = (dateTime: string): number => {
  return toUnix(new Date(dateTime));
};

/**
 * Convert RRuleSet from GraphQL backend to RRuleState for UI
 */
export default function toRRuleState(rset: RRuleSet): RRuleState {
  const rule: RRule = rset.rule;

  // Initialize with defaults
  const state: RRuleState = {
    cadence: 1,
    occurence: 1,
    days: { ...DEFAULT_RRULE_DAYS_STATE },
  };

  // Map frequency to cadence
  if (rule.FREQ) {
    state.cadence = FREQ_TO_CADENCE[rule.FREQ];
  }

  // Map interval to occurence
  if (rule.INTERVAL !== null && rule.INTERVAL !== undefined) {
    state.occurence = rule.INTERVAL;
  }

  // Handle BYDAY for weekly recurrence
  if (state.cadence === 7 && rule.BYDAY && rule.BYDAY.length > 0) {
    const days: RruleDaysState = { ...DEFAULT_RRULE_DAYS_STATE };
    rule.BYDAY.forEach((byDay) => {
      const weekdayIndex = RRULE_DAY_TO_WEEKDAY[byDay.day];
      if (weekdayIndex !== undefined) {
        days[weekdayIndex] = true;
      }
    });
    state.days = days;
  }

  // Handle BYDAY for monthly recurrence
  if (state.cadence === 30 && rule.BYDAY && rule.BYDAY.length > 0) {
    const firstByDay = rule.BYDAY[0];
    if (firstByDay) {
      state.daySelection = RRULE_DAY_TO_WEEKDAY[firstByDay.day];
      if (
        firstByDay.occurrence !== null &&
        firstByDay.occurrence !== undefined
      ) {
        state.dayOrder = firstByDay.occurrence;
      }
    }

    // BYSETPOS strategy: the position is defined by BYSETPOS
    const [setPosition] = rule.BYSETPOS ?? [];
    if (setPosition !== null && setPosition !== undefined) {
      state.dayOrder = setPosition;
      state.useBysetpos = true;
    }
  }

  // Handle start date
  if (rule.DTSTART) {
    state.startDate = parseDateTime(rule.DTSTART);
  }

  // Handle ending conditions
  if (rule.COUNT !== null && rule.COUNT !== undefined) {
    state.repeatAmount = rule.COUNT;
    state.endingType = "finish_after";
  } else if (rule.UNTIL) {
    state.endingDate = parseDateTime(rule.UNTIL);
    state.endingType = "finish_on";
  } else {
    // No COUNT or UNTIL means no end date
    state.endingType = "no_end_date";
  }

  return state;
}
